import { DialogComponent } from '@theme/dialog';
import { clamp, isMobileBreakpoint, prefersReducedMotion } from '@theme/utilities';
// Side-effect import: defines <drag-zoom-wrapper> (mobile pinch/double-tap zoom)
// so image slides can be wrapped without a separate script tag.
import './drag-zoom-wrapper.js';

const DESKTOP_ZOOM_SCALE = 2;

/**
 * Global media lightbox — a single instance is rendered by
 * snippets/lightbox.liquid in the layout and fed from ANY gallery.
 *
 * Opt-in per gallery: put `data-lightbox="<group-id>"` on the clickable
 * element (button or media container). The lightbox listens with one
 * document-level click delegation, so triggers survive full DOM swaps
 * (e.g. media-gallery replacing itself on variant:update). On open, the
 * group is collected fresh from the DOM in document order; hidden
 * duplicates (mobile slideshow vs. desktop grid render the same media
 * twice) are dropped via checkVisibility().
 *
 * Slide source per trigger (looked up in the trigger, or its parent when
 * the trigger is an overlay button):
 * - `deferred-media <template>` present → video slide. The template holds a
 *   finished <video>/<iframe> and no <script> nodes, so cloning is safe
 *   (see CLAUDE.md on cloned scripts executing on insert).
 * - otherwise the first <img> → image slide; `data_max_resolution` (PDP
 *   pattern) wins over the srcset for full-resolution zooming.
 *
 * Zoom: mobile via <drag-zoom-wrapper> (pinch + double-tap), desktop via
 * click-to-zoom with the image following the pointer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} track - The scroll-snap slide track.
 * @property {HTMLElement} counter - The "n/m" counter element.
 *
 * @extends DialogComponent
 */
export class MediaLightbox extends DialogComponent {
  requiredRefs = ['dialog', 'track', 'counter'];

  #controller = new AbortController();
  /** @type {IntersectionObserver | null} */
  #observer = null;
  #activeIndex = 0;
  /** @type {HTMLElement | null} */
  #zoomedSlide = null;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    document.addEventListener('click', this.#handleTriggerClick, { signal });
    this.addEventListener('keydown', this.#handleKeydown, { signal });
    this.refs.track.addEventListener('click', this.#handleTrackClick, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    this.#observer?.disconnect();
  }

  /**
   * Document-level delegation for `[data-lightbox]` triggers.
   *
   * @param {MouseEvent} event - The click event.
   */
  #handleTriggerClick = (event) => {
    if (!(event.target instanceof Element)) return;
    if (this.contains(event.target)) return;

    const trigger = event.target.closest('[data-lightbox]');
    if (!(trigger instanceof HTMLElement)) return;

    // A keyboard-activated trigger (Tab + Enter) matches :focus-visible at
    // click time — remembered so the ESC path knows whether the restored
    // focus ring is wanted (see closeDialog).
    this.#openedViaKeyboard = trigger.matches(':focus-visible');

    event.preventDefault();
    this.open(trigger);
  };

  /**
   * Opens the lightbox for the group of the given trigger.
   *
   * @param {HTMLElement} trigger - The clicked `[data-lightbox]` element.
   */
  open(trigger) {
    const triggers = this.#collectTriggers(trigger);
    if (!triggers.length) return;

    const startIndex = Math.max(0, triggers.indexOf(trigger));
    const slides = triggers
      .map((groupTrigger) => this.#buildSlide(groupTrigger))
      .filter((slide) => slide !== null);

    if (!slides.length) return;

    this.refs.track.replaceChildren(...slides);
    // Pointer session: suppress ALL focus rings inside the dialog (CSS) —
    // Chromium paints them on dialog focus moves even for mouse flows.
    // A Tab press lifts the suppression (see #handleKeydown).
    this.refs.dialog.classList.toggle('lightbox--pointer', !this.#openedViaKeyboard);
    this.showDialog();

    // showDialog defers showModal() to the next animation frame — the track
    // is only measurable (and scrollable) one frame after that.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.#scrollToIndex(startIndex, 'instant');
        this.#setActive(startIndex);
        this.#observeSlides();
      });
    });
  }

  /**
   * Scrolls to the previous slide, wrapping to the last one at the start
   * (invoked via on:click). Wrap jumps are instant — a smooth scroll would
   * visibly rush across every slide in between.
   */
  previous() {
    const count = this.refs.track.children.length;
    if (count < 2) return;

    const wraps = this.#activeIndex === 0;
    this.#scrollToIndex(wraps ? count - 1 : this.#activeIndex - 1, wraps ? 'instant' : undefined);
  }

  /** Scrolls to the next slide, wrapping to the first one at the end. */
  next() {
    const count = this.refs.track.children.length;
    if (count < 2) return;

    const wraps = this.#activeIndex === count - 1;
    this.#scrollToIndex(wraps ? 0 : this.#activeIndex + 1, wraps ? 'instant' : undefined);
  }

  #closedByPointer = false;
  #openedViaKeyboard = false;

  /**
   * Pointer-initiated close (X button, empty-area click). Marks the close so
   * the focus the browser restores to the opener afterwards can be dropped —
   * Chromium paints the :focus-visible ring around the (tile-filling) trigger
   * button even after mouse interaction.
   */
  close() {
    this.#closedByPointer = true;
    this.closeDialog();
  }

  // Wrap the inherited instance-property closeDialog (assigned by
  // DialogComponent's field initializer, so it is already set when this
  // subclass field initializes) — ESC and outside-click call it directly.
  #baseCloseDialog = this.closeDialog;

  closeDialog = async () => {
    if (!this.refs.dialog.open) return;

    this.#observer?.disconnect();
    this.#resetDesktopZoom();
    for (const slide of this.refs.track.children) {
      this.#deactivateMedia(/** @type {HTMLElement} */ (slide));
    }

    await this.#baseCloseDialog();

    // Slides are throwaway clones — drop them so the next open starts clean.
    this.refs.track.replaceChildren();

    // dialog.close() has synchronously restored focus to the opener — with a
    // forced Chromium focus ring around the tile-filling trigger button. Drop
    // it unless the lightbox was OPENED via keyboard (only real keyboard
    // users need the ring to see where they are; a mouse user pressing ESC
    // does not).
    const dropRestoredFocus = this.#closedByPointer || !this.#openedViaKeyboard;
    this.#closedByPointer = false;

    if (dropRestoredFocus && document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  };

  /**
   * Collects the visible triggers of the clicked trigger's group.
   *
   * Scope: within the trigger's own <dialog> when it lives in one (e.g. the
   * quickview sheet), otherwise the page without any dialog content — the
   * quickview PDP fragment could otherwise leak into the page's group when
   * both render the same product section.
   *
   * @param {HTMLElement} trigger - The clicked trigger.
   * @returns {HTMLElement[]} The triggers of the group, in DOM order.
   */
  #collectTriggers(trigger) {
    const group = trigger.getAttribute('data-lightbox') ?? '';
    const root = trigger.closest('dialog') ?? document;
    const selector = `[data-lightbox="${CSS.escape(group)}"]`;

    return /** @type {HTMLElement[]} */ ([...root.querySelectorAll(selector)]).filter((element) => {
      if (root === document && element.closest('dialog')) return false;
      return typeof element.checkVisibility === 'function' ? element.checkVisibility() : element.offsetParent !== null;
    });
  }

  /**
   * Builds one slide for a trigger.
   *
   * @param {HTMLElement} trigger - A `[data-lightbox]` element.
   * @returns {HTMLLIElement | null} The slide, or null when no media was found.
   */
  #buildSlide(trigger) {
    // Overlay buttons (PDP grid, story gallery) are empty — their media
    // sibling lives in the parent tile.
    const scope = trigger.matches('button, a') ? trigger.parentElement ?? trigger : trigger;

    const slide = document.createElement('li');
    slide.className = 'lightbox__slide';

    const template = scope.querySelector('deferred-media template');
    if (template instanceof HTMLTemplateElement) {
      slide.dataset.type = 'video';
      slide.append(template.content.cloneNode(true));

      // The gallery markup autoplays — in the lightbox only the ACTIVE slide
      // may play. Videos wait for play(); iframes park their src in data-src
      // (an autoplay=1 embed would start the moment it is inserted).
      for (const video of slide.querySelectorAll('video')) {
        video.removeAttribute('autoplay');
        video.setAttribute('controls', '');
        video.setAttribute('playsinline', '');
      }
      for (const iframe of slide.querySelectorAll('iframe')) {
        iframe.dataset.src = iframe.getAttribute('src') ?? '';
        iframe.removeAttribute('src');
      }
      return slide;
    }

    // Inline-autoplay gallery videos (<video-toggle>, see
    // product/media-video-inline.liquid): no template to stamp — clone the
    // element itself. muted/loop stay on (controls let the user unmute);
    // #activateMedia makes sure only the ACTIVE slide plays.
    const inlineVideo = scope.querySelector('video-toggle video');
    if (inlineVideo instanceof HTMLVideoElement) {
      slide.dataset.type = 'video';
      const copy = /** @type {HTMLVideoElement} */ (inlineVideo.cloneNode(true));
      copy.removeAttribute('autoplay');
      copy.setAttribute('controls', '');
      copy.setAttribute('playsinline', '');
      slide.append(copy);
      return slide;
    }

    const img = scope.querySelector('img');
    if (!img) return null;

    slide.dataset.type = 'image';

    const copy = document.createElement('img');
    const maxResolution = img.getAttribute('data_max_resolution');

    if (maxResolution) {
      copy.src = maxResolution;
    } else {
      copy.src = img.currentSrc || img.src;
      if (img.srcset) {
        copy.srcset = img.srcset;
        copy.sizes = '100vw';
      }
    }
    copy.alt = img.alt;
    copy.loading = 'lazy';
    copy.decoding = 'async';

    const wrapper = document.createElement('drag-zoom-wrapper');
    wrapper.className = 'lightbox__zoom';
    wrapper.append(copy);
    slide.append(wrapper);

    return slide;
  }

  /**
   * @param {number} index - The target slide index (clamped).
   * @param {ScrollBehavior} [behavior] - Defaults to smooth unless reduced motion.
   */
  #scrollToIndex(index, behavior) {
    const { track } = this.refs;
    const count = track.children.length;
    if (!count) return;

    const target = clamp(index, 0, count - 1);
    track.scrollTo({
      left: target * track.clientWidth,
      behavior: behavior ?? (prefersReducedMotion() ? 'instant' : 'smooth'),
    });
  }

  #observeSlides() {
    this.#observer?.disconnect();
    this.#observer = new IntersectionObserver(this.#handleIntersect, {
      root: this.refs.track,
      threshold: 0.6,
    });

    for (const slide of this.refs.track.children) {
      this.#observer.observe(slide);
    }
  }

  /** @param {IntersectionObserverEntry[]} entries */
  #handleIntersect = (entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;

      const index = [...this.refs.track.children].indexOf(entry.target);
      if (index !== -1 && index !== this.#activeIndex) this.#setActive(index);
    }
  };

  /**
   * Activates a slide: counter, media playback, zoom reset on the rest.
   *
   * @param {number} index - The now-active slide index.
   */
  #setActive(index) {
    this.#activeIndex = index;

    const slides = /** @type {HTMLElement[]} */ ([...this.refs.track.children]);
    // Localized "%index% von %length%" template from the host (see lightbox.liquid);
    // falls back to a bare "n/m" if the attribute is absent.
    const template = this.dataset.counterTemplate ?? '%index%/%length%';
    this.refs.counter.textContent = template
      .replace('%index%', String(index + 1))
      .replace('%length%', String(slides.length));

    slides.forEach((slide, slideIndex) => {
      if (slideIndex === index) {
        this.#activateMedia(slide);
      } else {
        this.#deactivateMedia(slide);
        this.#resetSlideZoom(slide);
      }
    });

    if (this.#zoomedSlide && this.#zoomedSlide !== slides[index]) this.#resetDesktopZoom();
  }

  /** @param {HTMLElement} slide */
  #activateMedia(slide) {
    for (const video of slide.querySelectorAll('video')) {
      video.play()?.catch(() => {});
    }
    for (const iframe of slide.querySelectorAll('iframe')) {
      if (!iframe.getAttribute('src') && iframe.dataset.src) iframe.src = iframe.dataset.src;
    }
  }

  /**
   * Stops playback: pause for <video>, drop the src for embeds — the
   * pause postMessage APIs differ per host, unloading is the reliable stop.
   *
   * @param {HTMLElement} slide
   */
  #deactivateMedia(slide) {
    for (const video of slide.querySelectorAll('video')) {
      video.pause();
    }
    for (const iframe of slide.querySelectorAll('iframe')) {
      if (iframe.getAttribute('src')) iframe.removeAttribute('src');
    }
  }

  /** @param {HTMLElement} slide */
  #resetSlideZoom(slide) {
    const wrapper = slide.querySelector('drag-zoom-wrapper');
    if (wrapper && 'resetZoom' in wrapper && typeof wrapper.resetZoom === 'function') wrapper.resetZoom();
  }

  /**
   * Track clicks: desktop click-to-zoom on images, close on empty area.
   *
   * @param {MouseEvent} event - The click event.
   */
  #handleTrackClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const slide = /** @type {HTMLElement | null} */ (event.target.closest('.lightbox__slide'));
    if (!slide) return;

    const image = event.target.closest('img');

    if (image && slide.dataset.type === 'image' && !isMobileBreakpoint()) {
      this.#toggleDesktopZoom(slide, event);
      return;
    }

    // Click on the letterboxed area around the media closes — mirrors the
    // backdrop-click convention (the dialog itself is fullscreen, so the
    // inherited outside-click close can never fire).
    if (!image && !event.target.closest('video, iframe, deferred-media, button')) {
      this.close();
    }
  };

  /**
   * @param {HTMLElement} slide - The clicked image slide.
   * @param {MouseEvent} event - The click event (zoom origin).
   */
  #toggleDesktopZoom(slide, event) {
    if (this.#zoomedSlide === slide) {
      this.#resetDesktopZoom();
      return;
    }

    this.#resetDesktopZoom();
    this.#zoomedSlide = slide;
    slide.classList.add('is-zoomed');
    slide.style.setProperty('--lightbox-zoom-scale', `${DESKTOP_ZOOM_SCALE}`);
    this.#panDesktopZoom(event);
    slide.addEventListener('pointermove', this.#panDesktopZoom, { signal: this.#controller.signal });
  }

  /**
   * The zoomed image follows the pointer: the transform-origin tracks the
   * cursor position relative to the (untransformed) slide box.
   *
   * @param {MouseEvent | PointerEvent} event
   */
  #panDesktopZoom = (event) => {
    const slide = this.#zoomedSlide;
    const image = slide?.querySelector('img');
    if (!slide || !image) return;

    const rect = slide.getBoundingClientRect();
    const x = clamp(((event.clientX - rect.left) / rect.width) * 100, 0, 100);
    const y = clamp(((event.clientY - rect.top) / rect.height) * 100, 0, 100);
    image.style.transformOrigin = `${x}% ${y}%`;
  };

  #resetDesktopZoom() {
    const slide = this.#zoomedSlide;
    if (!slide) return;

    slide.classList.remove('is-zoomed');
    slide.style.removeProperty('--lightbox-zoom-scale');
    slide.removeEventListener('pointermove', this.#panDesktopZoom);
    const image = slide.querySelector('img');
    image?.style.removeProperty('transform-origin');
    this.#zoomedSlide = null;
  }

  /** @param {KeyboardEvent} event */
  #handleKeydown = (event) => {
    if (!this.refs.dialog.open) return;

    if (event.key === 'Tab') {
      // The user switched to keyboard mid-session — focus rings back on.
      this.refs.dialog.classList.remove('lightbox--pointer');
      this.#openedViaKeyboard = true;
      return;
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.previous();
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.next();
    }
  };
}

if (!customElements.get('media-lightbox')) {
  customElements.define('media-lightbox', MediaLightbox);
}
